import { Link } from 'wouter';
import {
  MessageSquare,
  Zap,
  Code2,
  Webhook,
  LineChart,
  Shield,
  ArrowRight,
  Check,
  Sparkles,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white pt-20 pb-32">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full mb-8">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-semibold">
                Natural Language Time-Series Monitoring
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Monitor Your Data with
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-500">
                Plain English
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              API-first time-series platform for you and your clients. Push, manage, and query
              sensor data with isolated buckets—all controlled via simple REST APIs and natural language.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link href="/signup">
                <a className="w-full sm:w-auto px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all hover:shadow-lg flex items-center justify-center">
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Link>
              <a
                href="https://docs.dakkio.io"
                className="w-full sm:w-auto px-8 py-4 bg-white border-2 border-slate-200 text-slate-900 rounded-lg font-semibold hover:border-slate-300 transition-all"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Documentation
              </a>
            </div>

            {/* Social Proof / Quick Info */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600">
              <div className="flex items-center">
                <Check className="w-5 h-5 text-secondary-600 mr-2" />
                Free tier available
              </div>
              <div className="flex items-center">
                <Check className="w-5 h-5 text-secondary-600 mr-2" />
                No credit card required
              </div>
              <div className="flex items-center">
                <Check className="w-5 h-5 text-secondary-600 mr-2" />
                5-minute setup
              </div>
            </div>
          </div>

          {/* Hero Visual Placeholder */}
          {/* <div className="mt-16 max-w-5xl mx-auto">
            <div className="relative bg-gradient-to-br from-primary-600 to-secondary-500 rounded-2xl shadow-2xl aspect-video flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 bg-slate-900/20"></div>
              <div className="relative text-white/80 text-center p-8">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-60" />
                <p className="text-lg font-medium">
                  Product screenshot / demo video placeholder
                </p>
                <p className="text-sm mt-2 opacity-75">
                  Show natural language interface in action
                </p>
              </div>
            </div>
          </div> */}
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Stop Wrestling with Complex Monitoring Tools
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Traditional monitoring platforms require learning query languages,
                configuring dashboards, and writing complex rules. Managing data for
                multiple clients makes it even harder with complicated tenant isolation.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-900">With dakkio,</span> use our
                simple REST API to push and query data for any client. Each gets an isolated
                bucket, and you can describe alerts in plain English—no complex configuration needed.
              </p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
                  <p className="text-slate-600 mb-2 text-sm font-medium">Traditional:</p>
                  <code className="text-xs text-slate-900 bg-slate-100 px-2 py-1 rounded font-mono block">
                    SELECT avg(value) FROM sensors WHERE type='temp' AND value &gt; 30
                    GROUP BY time(5m)
                  </code>
                </div>
                <div className="text-center text-slate-400">
                  <ArrowRight className="w-5 h-5 mx-auto" />
                </div>
                <div className="bg-primary-50 rounded-lg p-4 border-2 border-primary-200">
                  <p className="text-primary-700 mb-2 text-sm font-medium">
                    With dakkio:
                  </p>
                  <p className="text-slate-900 font-medium">
                    "Alert me if temperature exceeds 30°C"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Monitor Time-Series Data
            </h2>
            <p className="text-lg text-slate-600">
              From data ingestion to natural language alerts, dakkio provides the
              complete toolkit for modern data monitoring.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Natural Language Interface
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Create alerts, query data, and get insights using plain English. No
                query languages or complex syntax required.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                <LineChart className="w-6 h-6 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Time-Series Storage
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Store and query millions of data points with automatic downsampling,
                aggregation, and retention policies.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <Webhook className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Webhook Notifications
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Get notified via webhooks when conditions are met. Integrate with
                Slack, Discord, email, or any HTTP endpoint.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                <Code2 className="w-6 h-6 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Embeddable Widgets
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Drop beautiful, customizable charts into your product with a simple
                script tag or React component.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Complete API Control
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Push, manage, and query data for you and your clients via REST API. Each client gets an isolated bucket with dedicated API keys. Ingest thousands of data points per second.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Multi-Tenant & Secure
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Perfect for B2B2C platforms. Each of your clients gets a separate data bucket with complete isolation, dedicated API keys, and independent access control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-lg text-slate-600">
              Three simple steps to start monitoring your data with natural language.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-600 to-secondary-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Create Buckets for Your Clients
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Sign up and create isolated data buckets—one for you, one for each client. Each bucket has separate API keys and complete data isolation.
              </p>
              <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm text-slate-700">
                <span className="text-secondary-600">POST</span> /api/buckets
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-600 to-secondary-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Push & Manage Data via API
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Use our REST API to push data for any client's bucket, query historical data, manage schemas, and configure alerts—all programmatically.
              </p>
              <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm text-slate-700">
                client.<span className="text-primary-600">ingest</span>(bucketId, data)
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-600 to-secondary-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Ask in Plain English
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Create alerts, query data, or embed charts—all using natural language.
                No configuration needed.
              </p>
              <div className="bg-primary-50 rounded-lg p-4 text-slate-900 italic">
                "Show me temperature trends for the last week"
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">
              Start free, scale as you grow. No surprises, no hidden fees.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Tier */}
            <div className="bg-white rounded-xl p-8 border-2 border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Free</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-slate-900">$0</span>
                <span className="text-slate-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">3 devices</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">100k data points/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">7 days retention</span>
                </li>
              </ul>
              <Link href="/signup">
                <a className="block w-full text-center px-6 py-3 bg-slate-100 text-slate-900 rounded-lg font-semibold hover:bg-slate-200 transition-colors">
                  Get Started
                </a>
              </Link>
            </div>

            {/* Pro Tier */}
            <div className="bg-primary-600 rounded-xl p-8 border-2 border-primary-700 relative shadow-xl scale-105">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-secondary-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-white">$29</span>
                <span className="text-primary-100">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-white mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">25 devices</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-white mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">5M data points/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-white mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">90 days retention</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-white mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Natural language queries</span>
                </li>
              </ul>
              <Link href="/signup">
                <a className="block w-full text-center px-6 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
                  Start Free Trial
                </a>
              </Link>
            </div>

            {/* Business Tier */}
            <div className="bg-white rounded-xl p-8 border-2 border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Business</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-slate-900">$149</span>
                <span className="text-slate-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">250 devices</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">50M data points/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">1 year retention</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Priority support</span>
                </li>
              </ul>
              <Link href="/signup">
                <a className="block w-full text-center px-6 py-3 bg-slate-100 text-slate-900 rounded-lg font-semibold hover:bg-slate-200 transition-colors">
                  Get Started
                </a>
              </Link>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link href="/pricing">
              <a className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center">
                View full pricing details
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-secondary-500">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Simplify Your Monitoring?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join developers who are ditching complex query languages for natural
            language monitoring.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/signup">
              <a className="w-full sm:w-auto px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl flex items-center justify-center">
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Link>
            <a
              href="https://docs.dakkio.io"
              className="w-full sm:w-auto px-8 py-4 bg-primary-700 text-white rounded-lg font-semibold hover:bg-primary-800 transition-all border-2 border-white/20 flex items-center justify-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read Documentation
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
